<template>
  <div class="stage">
    <div class="viewport">
      <div class="phone" dir="rtl">
        <offline-banner :visible="offline" />

        <countdown-timer :expires-at="expiresAt" @expired="onSessionExpired" />

        <router-view />

        <resume-dialog
          :open="resumeOpen"
          :step="resumeStep"
          @resume="onResume"
          @restart="onRestart"
        />
      </div>
    </div>
  </div>
</template>

<script>
import OfflineBanner from "@/components/OfflineBanner.vue";
import CountdownTimer from "@/components/CountdownTimer.vue";
import ResumeDialog from "@/components/ResumeDialog.vue";
import { restoreProgress } from "@/store/persist";
import { FLOW_ORDER } from "@/constants";
import { primeCue } from "@/utils/cue";
import { loadFaceDetector } from "@/utils/face";

/**
 * App shell: the phone-shaped frame, the ambient chrome that outlives any single screen,
 * and the two app-level decisions — session expiry and resuming an interrupted run.
 *
 * There is deliberately no auth check here. That moved to a router guard, so a protected
 * view is never created (and never opens a camera) before the session is verified.
 */
export default {
  name: "App",
  components: { OfflineBanner, CountdownTimer, ResumeDialog },

  data() {
    return { resumeOpen: false };
  },

  computed: {
    offline() {
      return this.$store.state.ui.offline;
    },
    expiresAt() {
      return this.$store.state.session.expiresAt;
    },
    resumeStep() {
      return this.$store.state.flow.resumeAt;
    },
  },

  created() {
    this.$store.dispatch("session/restore");
    this.applyStoredTheme();

    // Restoring is not the same as resuming: the state is put back either way so the
    // router guard sees an accurate picture, but the user is asked before being moved.
    const restored = restoreProgress(this.$store);
    if (restored && this.$store.getters["session/isAuthenticated"] && this.$store.getters["flow/canResume"]) {
      this.resumeOpen = true;
    }

    window.addEventListener("online", this.onConnectivityChange);
    window.addEventListener("offline", this.onConnectivityChange);
  },

  beforeDestroy() {
    window.removeEventListener("online", this.onConnectivityChange);
    window.removeEventListener("offline", this.onConnectivityChange);
  },

  methods: {
    onConnectivityChange() {
      this.$store.dispatch("ui/refreshOnline");
    },

    applyStoredTheme() {
      const theme = this.$store.state.ui.theme;
      if (theme) document.documentElement.setAttribute("data-theme", theme);
    },

    onResume() {
      // Resuming jumps straight to a camera step, so the welcome screen's button — which
      // normally wakes the audio and starts the detector download — is never pressed. This
      // tap is the substitute, and it is the only one we are going to get.
      primeCue();
      loadFaceDetector().catch(() => {});

      this.resumeOpen = false;
      const target = this.resumeStep;
      // Only ever resume onto a real flow step; anything else falls back to the guard's
      // own notion of how far the user actually got.
      const name =
        FLOW_ORDER.indexOf(target) !== -1 ? target : this.$store.getters["flow/furthestStep"];
      this.$router.push({ name });
    },

    async onRestart() {
      this.resumeOpen = false;
      await this.$store.dispatch("flow/reset");
      this.$router.push({ name: "start" });
    },

    async onSessionExpired() {
      await this.$store.dispatch("flow/reset");
      await this.$store.dispatch("session/clear");
      this.$router.push({ name: "start" });
    },
  },
};
</script>

<style scoped>
.stage {
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
}

.viewport {
  flex: 1;
  display: grid;
  place-items: center;
  min-height: 0;
}

/* Desktop keeps the exact mobile layout — a max-width column centred on the page, on the
   app's own background. No device mock-up: this ships to phones, and a fake bezel would
   only make the desktop case look like a preview of something else. */
.phone {
  position: relative;
  width: 100%;
  max-width: 420px;
  height: 100dvh;
  background: var(--background);
  overflow: hidden;
  isolation: isolate;
}
</style>

<style>
/* Views are absolutely-positioned panels inside `.phone`, so this rule has to reach the
   routed component's root element. */
.screen {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  padding: 22px 22px calc(20px + env(safe-area-inset-bottom));
}
</style>
